import {
  Braces,
  Check,
  ChevronRight,
  Clipboard,
  Clock3,
  Database,
  KeyRound,
  LoaderCircle,
  Play,
  Search,
  ShieldCheck,
  TableProperties,
  TerminalSquare,
  TriangleAlert
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionStatus } from '../shared/portalTypes';

export const WIDGET_TABLE_NAMES = [
  'biAgedPipelineCore',
  'biBookingsCore',
  'biManufacturerCloseRateCore',
  'biManufacturerPipelineCore'
] as const;

export type WidgetTableName = typeof WIDGET_TABLE_NAMES[number];
type JsonRecord = Record<string, unknown>;

interface MetadataExecutionResponse {
  toolName: string;
  result: JsonRecord;
  durationMs: number;
  correlationId: string;
}

export function McpMetadataWorkbench({
  toolName,
  sessionStatus,
  initialWidgetTable,
  onConnect,
  onInspectWidgetTable
}: {
  toolName: string;
  sessionStatus: SessionStatus;
  initialWidgetTable: WidgetTableName;
  onConnect(): void;
  onInspectWidgetTable(tableName: WidgetTableName): void;
}): ReactNode {
  const [tableName, setTableName] = useState<WidgetTableName>(initialWidgetTable);
  const [execution, setExecution] = useState<MetadataExecutionResponse>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState<'structured' | 'json'>('structured');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setTableName(initialWidgetTable);
  }, [initialWidgetTable]);

  const runTool = async () => {
    if (!sessionStatus.connected) {
      onConnect();
      return;
    }

    setRunning(true);
    setError('');
    setExecution(undefined);
    try {
      const response = await fetch('/portal/mcp/metadata/execute', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toolName,
          arguments: toolName === 'describe_widget_table' ? { tableName } : {}
        })
      });
      const body = await response.json().catch(() => undefined) as JsonRecord | undefined;
      if (!response.ok) {
        throw new Error(text(body, 'detail') || text(body, 'error') || `MCP execution returned ${response.status}.`);
      }
      setExecution(body as unknown as MetadataExecutionResponse);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The metadata tool could not be completed.');
    } finally {
      setRunning(false);
    }
  };

  const copyResult = async () => {
    if (!execution) return;
    await navigator.clipboard.writeText(JSON.stringify(execution.result, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <section className="mcp-run-workbench" aria-label={`Run ${toolName}`}>
      <header className="mcp-run-commandbar">
        <div className="mcp-run-context">
          <span><ShieldCheck aria-hidden="true" /> Read only</span>
          <strong>{sessionStatus.environment.toUpperCase()}</strong>
          <code>{sessionStatus.customerCode ?? 'No customer selected'}</code>
        </div>
        <button className="mcp-run-button" type="button" disabled={running} onClick={() => void runTool()}>
          {running ? <LoaderCircle className="spinning" aria-hidden="true" /> : <Play aria-hidden="true" />}
          <span>{running ? 'Running' : sessionStatus.connected ? 'Run tool' : 'Connect to run'}</span>
        </button>
      </header>

      <div className="mcp-run-body">
        <section className="mcp-run-inputs" aria-labelledby={`mcp-run-inputs-title-${toolName}`}>
          <div className="mcp-run-section-title">
            <TerminalSquare aria-hidden="true" />
            <div><h4 id={`mcp-run-inputs-title-${toolName}`}>Inputs</h4><p>Validated before the MCP session starts.</p></div>
          </div>
          {toolName === 'describe_widget_table' ? (
            <label className="mcp-run-field">
              <span>Widget table <em>Required</em></span>
              <select value={tableName} onChange={(event) => setTableName(event.currentTarget.value as WidgetTableName)}>
                {WIDGET_TABLE_NAMES.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
          ) : (
            <div className="mcp-run-zero-input">
              <Check aria-hidden="true" />
              <span><strong>No arguments required</strong><small>This discovery tool is ready to run.</small></span>
            </div>
          )}
          <div className="mcp-run-invocation">
            <span>MCP invocation</span>
            <code>{toolName}({toolName === 'describe_widget_table' ? ` tableName: "${tableName}" ` : ''})</code>
          </div>
        </section>

        <section className="mcp-run-results" aria-live="polite">
          <div className="mcp-run-resultbar">
            <div>
              <Database aria-hidden="true" />
              <span><strong>Result</strong><small>{execution ? `Completed in ${execution.durationMs.toLocaleString()} ms` : 'Structured MCP response'}</small></span>
            </div>
            {execution && (
              <div className="mcp-result-actions">
                <div className="mcp-result-view-toggle" aria-label="Result view">
                  <button type="button" className={view === 'structured' ? 'selected' : undefined} onClick={() => setView('structured')}>View</button>
                  <button type="button" className={view === 'json' ? 'selected' : undefined} onClick={() => setView('json')}>JSON</button>
                </div>
                <button type="button" title="Copy result JSON" onClick={() => void copyResult()}>{copied ? <Check aria-hidden="true" /> : <Clipboard aria-hidden="true" />}</button>
              </div>
            )}
          </div>

          {running && <McpResultLoading toolName={toolName} />}
          {!running && error && <div className="mcp-run-error" role="alert"><TriangleAlert aria-hidden="true" /><span><strong>Execution failed</strong><p>{error}</p></span></div>}
          {!running && !error && !execution && <McpResultEmpty connected={sessionStatus.connected} />}
          {!running && execution && view === 'json' && <pre className="mcp-result-json">{JSON.stringify(execution.result, null, 2)}</pre>}
          {!running && execution && view === 'structured' && (
            <MetadataResult toolName={toolName} result={execution.result} onInspectWidgetTable={onInspectWidgetTable} />
          )}
          {execution && <footer className="mcp-result-footer"><Clock3 aria-hidden="true" /><span>Correlation</span><code>{execution.correlationId}</code></footer>}
        </section>
      </div>
    </section>
  );
}

function MetadataResult({ toolName, result, onInspectWidgetTable }: {
  toolName: string;
  result: JsonRecord;
  onInspectWidgetTable(tableName: WidgetTableName): void;
}): ReactNode {
  if (toolName === 'describe_analytics_schema') return <AnalyticsSchemaResult result={result} />;
  if (toolName === 'list_widget_tables') return <WidgetTableListResult result={result} onInspect={onInspectWidgetTable} />;
  return <WidgetTableDescriptionResult result={result} />;
}

function AnalyticsSchemaResult({ result }: { result: JsonRecord }): ReactNode {
  const datasets = records(result, 'datasets');
  const [selectedName, setSelectedName] = useState(text(datasets[0], 'name'));
  const [query, setQuery] = useState('');
  const dataset = datasets.find((item) => text(item, 'name') === selectedName) ?? datasets[0];
  const dimensions = filterFields(records(dataset, 'dimensions'), query);
  const measures = filterFields(records(dataset, 'measures'), query);

  return (
    <div className="mcp-analytics-result">
      <div className="mcp-result-summary-strip">
        <span><strong>{datasets.length}</strong><small>Datasets</small></span>
        <span><strong>{records(dataset, 'dimensions').length}</strong><small>Dimensions</small></span>
        <span><strong>{records(dataset, 'measures').length}</strong><small>Measures</small></span>
        <span><strong>{strings(result, 'safetyRules').length}</strong><small>Safety rules</small></span>
      </div>
      <div className="mcp-dataset-tabs" role="tablist" aria-label="Analytics datasets">
        {datasets.map((item) => {
          const name = text(item, 'name');
          return <button type="button" role="tab" aria-selected={name === selectedName} className={name === selectedName ? 'selected' : undefined} key={name} onClick={() => setSelectedName(name)}>{name}</button>;
        })}
      </div>
      {dataset && <>
        <p className="mcp-dataset-description">{text(dataset, 'description')}</p>
        <label className="mcp-result-search"><Search aria-hidden="true" /><span className="sr-only">Search dimensions and measures</span><input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search dimensions and measures" /></label>
        <div className="mcp-schema-field-grid">
          <SchemaFieldList title="Dimensions" items={dimensions} tone="dimension" />
          <SchemaFieldList title="Measures" items={measures} tone="measure" />
        </div>
        <div className="mcp-schema-guidance-grid">
          <GuidanceList title="Defaults" items={strings(dataset, 'defaults')} />
          <GuidanceList title="Examples" items={strings(dataset, 'examples')} code />
        </div>
      </>}
    </div>
  );
}

function SchemaFieldList({ title, items, tone }: { title: string; items: JsonRecord[]; tone: string }): ReactNode {
  return <section className={`mcp-schema-fields ${tone}`}><header><h5>{title}</h5><span>{items.length}</span></header><div>{items.map((item) => <article key={text(item, 'name')}><code>{text(item, 'name')}</code><p>{text(item, 'description')}</p>{strings(item, 'aliases').length > 0 && <small>Also: {strings(item, 'aliases').join(', ')}</small>}</article>)}</div></section>;
}

function GuidanceList({ title, items, code = false }: { title: string; items: string[]; code?: boolean }): ReactNode {
  return <section className="mcp-guidance-list"><h5>{title}</h5><ul>{items.map((item) => <li key={item}>{code ? <code>{item}</code> : item}</li>)}</ul></section>;
}

function WidgetTableListResult({ result, onInspect }: { result: JsonRecord; onInspect(tableName: WidgetTableName): void }): ReactNode {
  const tables = records(result, 'tables');
  return <div className="mcp-widget-list-result"><div className="mcp-result-summary-strip"><span><strong>{tables.length}</strong><small>Widget tables</small></span><span><strong>Hourly</strong><small>Production cadence</small></span><span><strong>Read only</strong><small>Discovery surface</small></span></div><div className="mcp-widget-table-list">{tables.map((table) => {
    const tableName = text(table, 'tableName') as WidgetTableName;
    return <article key={tableName}><span className="mcp-widget-table-icon"><TableProperties aria-hidden="true" /></span><div><code>{tableName}</code><h5>{text(table, 'displayName')}</h5><p>{text(table, 'purpose')}</p><small>{text(table, 'grain')}</small></div><button type="button" onClick={() => onInspect(tableName)}><span>Inspect schema</span><ChevronRight aria-hidden="true" /></button></article>;
  })}</div></div>;
}

function WidgetTableDescriptionResult({ result }: { result: JsonRecord }): ReactNode {
  const table = record(result, 'table');
  if (!table) return <div className="mcp-run-error"><TriangleAlert aria-hidden="true" /><span><strong>Table unavailable</strong><p>{text(result, 'error')}</p></span></div>;
  const columns = records(table, 'columns');
  const primaryKey = strings(table, 'primaryKey');
  return <div className="mcp-widget-description-result"><header><span className="mcp-widget-table-icon"><TableProperties aria-hidden="true" /></span><div><code>{text(table, 'tableName')}</code><h4>{text(table, 'displayName')}</h4><p>{text(table, 'purpose')}</p></div></header><div className="mcp-result-summary-strip"><span><strong>{columns.length}</strong><small>Columns</small></span><span><strong>{primaryKey.length}</strong><small>Primary key fields</small></span><span><strong>{text(table, 'refreshCadence')}</strong><small>Freshness</small></span></div><div className="mcp-widget-grain"><Database aria-hidden="true" /><span><strong>Grain</strong>{text(table, 'grain')}</span></div><div className="mcp-column-table"><div className="mcp-column-table-head"><span>Column</span><span>SQL type</span><span>Nullable</span><span>Key</span></div>{columns.map((column) => <article key={text(column, 'name')}><span><code>{text(column, 'name')}</code><small>{text(column, 'description')}</small></span><code>{text(column, 'sqlType')}</code><span>{bool(column, 'nullable') ? 'Yes' : 'No'}</span><span>{bool(column, 'isPrimaryKey') ? <><KeyRound aria-hidden="true" /> PK</> : '—'}</span></article>)}</div></div>;
}

function McpResultLoading({ toolName }: { toolName: string }): ReactNode {
  return <div className="mcp-result-loading"><LoaderCircle className="spinning" aria-hidden="true" /><strong>Running {toolName}</strong><span>Opening a secure MCP session and validating the structured result.</span><div><i /><i /><i /></div></div>;
}

function McpResultEmpty({ connected }: { connected: boolean }): ReactNode {
  return <div className="mcp-result-empty"><Braces aria-hidden="true" /><strong>{connected ? 'Ready for evidence' : 'Connect to your MCP scope'}</strong><span>{connected ? 'Run the tool to inspect its live metadata contract.' : 'Your portal session supplies authorization without exposing a token to the browser.'}</span></div>;
}

function filterFields(items: JsonRecord[], query: string): JsonRecord[] {
  const normalized = query.trim().toLowerCase();
  return normalized ? items.filter((item) => JSON.stringify(item).toLowerCase().includes(normalized)) : items;
}

function record(candidate: unknown, name: string): JsonRecord | undefined {
  const source = asRecord(candidate);
  return asRecord(value(source, name));
}

function records(candidate: unknown, name: string): JsonRecord[] {
  const source = asRecord(candidate);
  const collection = value(source, name);
  return Array.isArray(collection) ? collection.map(asRecord).filter((item): item is JsonRecord => Boolean(item)) : [];
}

function strings(candidate: unknown, name: string): string[] {
  const source = asRecord(candidate);
  const collection = value(source, name);
  return Array.isArray(collection) ? collection.filter((item): item is string => typeof item === 'string') : [];
}

function text(candidate: unknown, name: string): string {
  const source = asRecord(candidate);
  const candidateValue = value(source, name);
  return typeof candidateValue === 'string' ? candidateValue : '';
}

function bool(candidate: unknown, name: string): boolean {
  const source = asRecord(candidate);
  return value(source, name) === true;
}

function value(source: JsonRecord | undefined, name: string): unknown {
  if (!source) return undefined;
  const key = Object.keys(source).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
  return key ? source[key] : undefined;
}

function asRecord(candidate: unknown): JsonRecord | undefined {
  return candidate && typeof candidate === 'object' && !Array.isArray(candidate) ? candidate as JsonRecord : undefined;
}